import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { fetchBySlug, getLocalized } from "../lib/cms";
import { CONTACT_INFO } from "../data";

export default function Profil() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadProfil() {
      try {
        const data = await fetchBySlug("profil");
        setPageData(data);
      } catch (err) {
        console.error("Fejl ved hentning af profil:", err);
      } finally {
        setLoading(false);
      }
    }
    loadProfil();
  }, []);

  const title = pageData ? getLocalized(pageData.title) : "Profil";
  const contentHtml = pageData ? getLocalized(pageData.content) : "";
  const metaTitle = pageData ? getLocalized(pageData.meta_title) : "Profil | Bachmann Tømrer";
  const metaDesc = pageData ? getLocalized(pageData.meta_description) : "Mød Martin Bachmann Andersen, manden bag Bachmann Tømrer. Uddannet tømrer med 14 års erfaring og fokus på kvalitet og kundetilfredshed.";

  return (
    <div className="animate-in fade-in duration-500">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        {pageData?.images?.[0] && <meta property="og:image" content={pageData.images[0]} />}
      </Helmet>

      {/* Page Hero */}
      <section className="relative py-24 overflow-hidden bg-brand-graphite text-white">
        {/* Background Image with Dark Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url(${pageData?.images?.[0] || 'https://assets.auxodev.dk/assets/4308d28a-24e2-4c25-a37f-97d46b6f1512/image00038-scaled.jpeg'})`,
          }}
        />
        <div className="absolute inset-0 bg-brand-graphite/75" />
        
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center z-10">
          <span className="text-xs font-bold uppercase tracking-widest text-brand-beige">Hvem er vi?</span>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white mt-2">
            {title}
          </h1>
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-white/70">
            <Link to="/" className="hover:text-brand-beige transition-colors">Forside</Link>
            <i className="fa-solid fa-chevron-right text-[8px]" />
            <span className="text-white font-medium">{title}</span>
          </div>
        </div>
      </section>

      {/* Profile Content */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-brown border-t-transparent" />
            </div>
          ) : pageData ? (
            <div className="space-y-16">
              {/* Top Section: Editorial Profile Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                {/* Text Content Column */}
                <div className="lg:col-span-12 space-y-6">
                  <div className="relative bg-brand-beige/20 rounded-2xl p-6 sm:p-10 border border-brand-brown/5 shadow-xs">
                    {/* Elegant quote icon or accent */}
                    <div className="absolute top-6 right-8 text-brand-brown/10 text-6xl font-serif select-none pointer-events-none">
                      ”
                    </div>
                    
                    <div 
                      className="prose prose-brand max-w-none text-brand-graphite/90 leading-relaxed text-sm sm:text-base space-y-6"
                      dangerouslySetInnerHTML={{ __html: contentHtml }}
                    />
                  </div>
                </div>
              </div>

              {/* Bottom Section: Kort om Bachmann Tømrer Info Box */}
              <div className="border-t border-brand-brown/10 pt-12">
                <div className="max-w-3xl mx-auto bg-brand-graphite text-white rounded-2xl p-8 sm:p-10 shadow-xl relative overflow-hidden">
                  {/* Decorative background pattern */}
                  <div className="absolute -right-10 -bottom-10 text-white/5 text-9xl font-serif select-none pointer-events-none">
                    BT
                  </div>
                  
                  <div className="relative z-10">
                    <div className="text-center mb-8">
                      <span className="text-xs font-bold uppercase tracking-widest text-brand-beige">Hurtigt overblik</span>
                      <h3 className="font-serif text-2xl font-bold mt-1">
                        Kort om Bachmann Tømrer
                      </h3>
                      <div className="w-12 h-0.5 bg-brand-beige mx-auto mt-3" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
                      <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-beige/10 text-brand-beige">
                          <i className="fa-solid fa-user text-lg" />
                        </div>
                        <div>
                          <p className="text-xs text-white/50 uppercase tracking-wider font-semibold">Ejer</p>
                          <p className="text-sm font-medium text-white mt-0.5">{CONTACT_INFO.owner}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-beige/10 text-brand-beige">
                          <i className="fa-solid fa-briefcase text-lg" />
                        </div>
                        <div>
                          <p className="text-xs text-white/50 uppercase tracking-wider font-semibold">Erfaring</p>
                          <p className="text-sm font-medium text-white mt-0.5">{CONTACT_INFO.experience}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-beige/10 text-brand-beige">
                          <i className="fa-solid fa-location-dot text-lg" />
                        </div>
                        <div>
                          <p className="text-xs text-white/50 uppercase tracking-wider font-semibold">Område</p>
                          <p className="text-sm font-medium text-white mt-0.5">Silkeborg, Ry, Kjellerup & omegn</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-beige/10 text-brand-beige">
                          <i className="fa-solid fa-id-card text-lg" />
                        </div>
                        <div>
                          <p className="text-xs text-white/50 uppercase tracking-wider font-semibold">CVR-nummer</p>
                          <p className="text-sm font-medium text-white mt-0.5">{CONTACT_INFO.cvr}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-brand-graphite/60">
              Profil kunne ikke indlæses.
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-beige-dark/30 border-t border-brand-brown/5">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-brand-graphite mb-4">
            Skal vi tage en snak om dit projekt?
          </h2>
          <p className="text-xs sm:text-sm text-brand-graphite/70 max-w-xl mx-auto mb-8 leading-relaxed">
            Vi står klar til at hjælpe dig videre, uanset om du har brug for rådgivning, et tilbud eller blot har et spørgsmål.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto rounded-full bg-brand-brown px-6 py-3 text-xs font-bold uppercase tracking-wider text-white shadow-md hover:bg-brand-brown-dark"
            >
              Kontakt os i dag
            </Link>
            <a
              href={`tel:${CONTACT_INFO.phoneRaw}`}
              className="w-full sm:w-auto rounded-full border border-brand-brown/30 bg-white px-6 py-3 text-xs font-bold uppercase tracking-wider text-brand-brown hover:bg-brand-beige"
            >
              Ring {CONTACT_INFO.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
